import { useState } from 'react';
import { api } from '../api';
import { ChartIcon } from '../components/icons';
import { CodeButton, Empty, ErrorNote, PageHeader } from '../components/ui';
import { useLoad } from '../hooks';

interface Day {
  day: string;
  sent: number;
  delivered: number;
  bounced: number;
  complained: number;
}

interface Metrics {
  days: Day[];
  totals: Omit<Day, 'day'>;
  rates: { delivered: number; bounced: number; complained: number };
  limits: { bounceRate: number; complaintRate: number };
  today: { sent: number; dailyLimit: number };
}

const SERIES = [
  { key: 'delivered', label: 'Delivered', color: '#4ade80' },
  { key: 'bounced', label: 'Bounced', color: '#f87171' },
  { key: 'complained', label: 'Complained', color: '#fbbf6a' },
  { key: 'other', label: 'Sent, no result yet', color: '#1d7fe0' },
] as const;

const pct = (n: number, digits = 1) => `${(n * 100).toFixed(digits).replace(/\.0+$/, '')}%`;
const dayLabel = (day: string) =>
  new Date(`${day}T00:00:00+05:30`).toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    timeZone: 'Asia/Kolkata',
  });

/** A round number at or above `n` for the chart's top line. */
function niceMax(n: number) {
  if (n <= 4) return 4;
  const step = 10 ** Math.floor(Math.log10(n));
  return Math.ceil(n / step) * step;
}

function Chart({ days }: { days: Day[] }) {
  const [hover, setHover] = useState<number | null>(null);
  const W = 1000;
  const H = 300;
  const left = 36;
  const bottom = 26;
  const max = niceMax(Math.max(...days.map((d) => d.sent)));
  const slot = (W - left) / days.length;
  const barW = Math.min(36, slot * 0.6);
  const y = (v: number) => (H - bottom) * (1 - v / max);
  const every = Math.ceil(days.length / 10);
  const hovered = hover === null ? null : days[hover];

  return (
    <div className="chart">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        role="img"
        aria-label="Emails per day"
      >
        <g className="grid">
          {[0, 0.25, 0.5, 0.75, 1].map((f) => (
            <line key={f} x1={left} x2={W} y1={y(max * f)} y2={y(max * f)} />
          ))}
        </g>
        <g className="axis">
          {[0, 0.5, 1].map((f) => (
            <text key={f} x={0} y={y(max * f) + 4}>
              {Math.round(max * f).toLocaleString('en-IN')}
            </text>
          ))}
          {days.map((d, i) =>
            i % every === 0 || i === days.length - 1 ? (
              <text key={d.day} x={left + slot * i + slot / 2} y={H - 6} textAnchor="middle">
                {dayLabel(d.day)}
              </text>
            ) : null,
          )}
        </g>
        {days.map((d, i) => {
          const other = Math.max(0, d.sent - d.delivered - d.bounced - d.complained);
          const parts = [d.delivered, d.bounced, d.complained, other];
          let top = H - bottom;
          const x = left + slot * i + (slot - barW) / 2;
          return (
            // biome-ignore lint/a11y/noStaticElementInteractions: hover shows the same numbers as the table below
            <g
              key={d.day}
              onMouseEnter={() => setHover(i)}
              onMouseLeave={() => setHover(null)}
              opacity={hover === null || hover === i ? 1 : 0.45}
            >
              <rect x={left + slot * i} y={0} width={slot} height={H - bottom} fill="transparent" />
              {d.sent === 0 ? (
                <rect
                  x={x}
                  y={H - bottom - 2}
                  width={barW}
                  height={2}
                  rx={1}
                  fill="rgba(255,255,255,0.08)"
                />
              ) : (
                parts.map((value, s) => {
                  if (!value) return null;
                  const h = ((H - bottom) * value) / max;
                  top -= h;
                  return (
                    <rect
                      key={SERIES[s]?.key}
                      x={x}
                      y={top}
                      width={barW}
                      height={h}
                      rx={3}
                      fill={SERIES[s]?.color}
                    />
                  );
                })
              )}
            </g>
          );
        })}
      </svg>
      {hovered && hover !== null ? (
        <div
          className="chart-tip"
          style={{
            left: `${Math.min(80, ((left + slot * hover + slot / 2) / W) * 100)}%`,
            top: 10,
          }}
        >
          <strong>{dayLabel(hovered.day)}</strong>
          <div>
            Sent <b>{hovered.sent.toLocaleString('en-IN')}</b>
          </div>
          <div>
            Delivered <b>{hovered.delivered.toLocaleString('en-IN')}</b>
          </div>
          <div>
            Bounced <b>{hovered.bounced.toLocaleString('en-IN')}</b>
          </div>
          <div>
            Complained <b>{hovered.complained.toLocaleString('en-IN')}</b>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function RateCard({
  title,
  rate,
  limit,
  digits,
  explain,
}: {
  title: string;
  rate: number;
  limit: number;
  digits: number;
  explain: string;
}) {
  // The bar shows 0 to twice the limit, so the limit mark sits in the middle.
  const fill = Math.min(1, rate / (limit * 2));
  const tone = rate > limit ? 'bad' : rate > limit * 0.5 ? 'warn' : '';
  return (
    <div className="card stat-card">
      <div className="label">{title}</div>
      <div className="big" style={{ color: tone === 'bad' ? 'var(--bad)' : undefined }}>
        {pct(rate, digits)}
      </div>
      <div className="meter">
        <div className={`fill ${tone}`} style={{ width: `${fill * 100}%` }} />
        <span className="mark" style={{ left: '50%' }} />
      </div>
      <div className="meter-caption">
        <span>0%</span>
        <span>Pause limit {pct(limit, digits)}</span>
        <span>{pct(limit * 2, digits)}</span>
      </div>
      <p className="note">{explain}</p>
    </div>
  );
}

export function Metrics() {
  const [days, setDays] = useState(15);
  const { data, error, loading } = useLoad(
    () => api<Metrics>('GET', `/metrics?days=${days}`),
    [days],
  );

  return (
    <>
      <PageHeader
        title="Metrics"
        description="Deliveries, bounces and complaints, and how close you are to the limits."
        actions={
          <>
            <select
              className="input"
              aria-label="Time range"
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
            >
              <option value={7}>Last 7 days</option>
              <option value={15}>Last 15 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
            </select>
            <CodeButton
              title="Get metrics"
              snippets={[
                {
                  label: 'cURL',
                  code: `curl "https://api.vaartix.com/metrics?days=${days}" \\\n  -H "Authorization: Bearer $VAARTIX_API_KEY"`,
                },
              ]}
            />
          </>
        }
      />
      <ErrorNote error={error} />
      {loading && !data ? (
        <div className="card" style={{ height: 420 }} />
      ) : data ? (
        <>
          <div className="card">
            <div className="stats">
              <div className="stat">
                <div className="label">Emails sent</div>
                <div className="value">{data.totals.sent.toLocaleString('en-IN')}</div>
              </div>
              <div className="stat">
                <div className="label">Delivered</div>
                <div className="value">{data.totals.sent ? pct(data.rates.delivered) : '—'}</div>
              </div>
              <div className="stat">
                <div className="label">Bounced</div>
                <div className="value">{data.totals.bounced.toLocaleString('en-IN')}</div>
              </div>
              <div className="stat">
                <div className="label">Complaints</div>
                <div className="value">{data.totals.complained.toLocaleString('en-IN')}</div>
              </div>
            </div>
            {data.totals.sent ? (
              <>
                <Chart days={data.days} />
                <div className="legend">
                  {SERIES.map((s) => (
                    <span key={s.key}>
                      <i style={{ background: s.color }} />
                      {s.label}
                    </span>
                  ))}
                </div>
              </>
            ) : (
              <div style={{ padding: 20 }}>
                <Empty icon={<ChartIcon />} title="Nothing sent in this period">
                  <p>
                    Metrics count real recipients. Emails to test addresses at{' '}
                    <code>test.vaartix.com</code> don't appear here.
                  </p>
                </Empty>
              </div>
            )}
          </div>

          <div className="grid-2">
            <RateCard
              title="Bounce rate"
              rate={data.rates.bounced}
              limit={data.limits.bounceRate}
              digits={1}
              explain="Addresses that don't exist. Keep it under 2%: only send to people who signed up, and never retry bounced addresses."
            />
            <RateCard
              title="Complaint rate"
              rate={data.rates.complained}
              limit={data.limits.complaintRate}
              digits={2}
              explain="Recipients who clicked “Report spam”. Gmail expects under 0.1%. Send only what people expect, from a name they recognise."
            />
          </div>

          <div className="card stat-card" style={{ marginTop: 16 }}>
            <div className="label">Sent today</div>
            <div className="big">
              {data.today.sent.toLocaleString('en-IN')}{' '}
              <span
                className="dim"
                style={{ fontSize: 18, fontFamily: 'var(--sans)', fontWeight: 400 }}
              >
                of {data.today.dailyLimit.toLocaleString('en-IN')} a day
              </span>
            </div>
            <div className="meter">
              <div
                className={`fill ${data.today.sent / data.today.dailyLimit > 0.9 ? 'warn' : ''}`}
                style={{
                  width: `${Math.min(100, (data.today.sent / data.today.dailyLimit) * 100)}%`,
                }}
              />
            </div>
            <p className="note">
              The limit resets at midnight IST and grows with a clean sending history. Need more
              today? Write to support@vaartix.com.
            </p>
          </div>
        </>
      ) : null}
    </>
  );
}
